import React, { useState } from 'react';
import './BulkUploadResultPopup.css';

const ITEMS_PER_PAGE = 5;

const BulkUploadResultPopup = ({ open, onClose, data, type = 'questions' }) => {
  const [failedPage, setFailedPage] = useState(1);
  const [successPage, setSuccessPage] = useState(1);

  if (!open || !data) return null;

  const hasSuccessfulRoles = data.successful_roles && data.successful_roles.length > 0;
  const hasFailedRoles = data.failed_roles && data.failed_roles.length > 0;
  const isError = data.status === 'error';
  const isPartialSuccess = data.status === 'partial_success';

  // Pagination for failed roles
  const failedRoles = data.failed_roles || [];
  const totalFailedPages = Math.ceil(failedRoles.length / ITEMS_PER_PAGE);
  const paginatedFailedRoles = failedRoles.slice(
    (failedPage - 1) * ITEMS_PER_PAGE,
    failedPage * ITEMS_PER_PAGE
  );

  // Pagination for successful roles
  const successfulRoles = data.successful_roles || [];
  const totalSuccessPages = Math.ceil(successfulRoles.length / ITEMS_PER_PAGE);
  const paginatedSuccessRoles = successfulRoles.slice(
    (successPage - 1) * ITEMS_PER_PAGE,
    successPage * ITEMS_PER_PAGE
  );

  const renderPagination = (currentPage, totalPages, setPage, label) => {
    if (totalPages <= 1) return null;
    
    return (
      <div className="bur-pagination">
        <span className="bur-pagination-info">
          Page {currentPage} of {totalPages} ({label})
        </span>
        <div className="bur-pagination-controls">
          <button
            className="bur-pagination-btn"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            ← Prev
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
            <button
              key={page}
              className={`bur-pagination-btn bur-page-num ${currentPage === page ? 'active' : ''}`}
              onClick={() => setPage(page)}
            >
              {page}
            </button>
          ))}
          <button
            className="bur-pagination-btn"
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
          >
            Next →
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="bur-overlay" onClick={onClose}>
      <div className="bur-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={`bur-header ${isError ? 'bur-header-error' : isPartialSuccess ? 'bur-header-partial' : 'bur-header-success'}`}>
          <div className="bur-header-icon">
            {isError ? (
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="15" y1="9" x2="9" y2="15" />
                <line x1="9" y1="9" x2="15" y2="15" />
              </svg>
            ) : isPartialSuccess ? (
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            ) : (
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <path d="M9 12l2 2 4-4" />
              </svg>
            )}
          </div>
          <h2 className="bur-title">
            {isError ? 'Upload Failed - Weightage Validation Error' : isPartialSuccess ? 'Partial Upload - Some Roles Uploaded' : 'Bulk Upload Completed'}
          </h2>
          <button className="bur-close-btn" onClick={onClose}>×</button>
        </div>

        {/* Summary */}
        <div className="bur-summary">
          <p className="bur-message">{data.message}</p>
          {data.summary && (
            <div className="bur-stats">
              {type === 'questions' ? (
                <>
                  <div className="bur-stat">
                    <span className="bur-stat-label">Total Roles in Excel:</span>
                    <span className="bur-stat-value">{data.summary.total_roles_in_excel || data.summary.roles_processed || 0}</span>
                  </div>
                  {data.summary.questions_created !== undefined && (
                    <div className="bur-stat bur-stat-success">
                      <span className="bur-stat-label">Questions Created:</span>
                      <span className="bur-stat-value">{data.summary.questions_created}</span>
                    </div>
                  )}
                  {data.summary.roles_failed !== undefined && (
                    <div className="bur-stat bur-stat-error">
                      <span className="bur-stat-label">Roles Failed:</span>
                      <span className="bur-stat-value">{data.summary.roles_failed}</span>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="bur-stat">
                    <span className="bur-stat-label">Roles Created:</span>
                    <span className="bur-stat-value">{data.summary.roles_created || 0}</span>
                  </div>
                  <div className="bur-stat bur-stat-success">
                    <span className="bur-stat-label">Categories Created:</span>
                    <span className="bur-stat-value">{data.summary.categories_created || 0}</span>
                  </div>
                  {data.summary.roles_failed !== undefined && (
                    <div className="bur-stat bur-stat-error">
                      <span className="bur-stat-label">Roles Failed:</span>
                      <span className="bur-stat-value">{data.summary.roles_failed}</span>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="bur-content">
          {/* Failed Roles Section */}
          {hasFailedRoles && (
            <div className="bur-section bur-section-error">
              <h3 className="bur-section-title">
                <span className="bur-section-icon">❌</span>
                Failed Roles - Weightage Errors ({failedRoles.length})
              </h3>
              <div className="bur-table-container">
                <table className="bur-table">
                  <thead>
                    <tr>
                      <th>Role</th>
                      <th>Department</th>
                      <th>Existing Weightage</th>
                      <th>Uploading Weightage</th>
                      <th>Required Weightage</th>
                      <th>Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedFailedRoles.map((role, idx) => (
                      <tr key={idx}>
                        <td className="bur-role-name">{role.role}</td>
                        <td>{role.department}</td>
                        <td>
                          <span className="bur-weightage bur-weightage-existing">
                            {role.existing_weightage}%
                          </span>
                        </td>
                        <td>
                          <span className={`bur-weightage ${role.error_type === 'exceeds' ? 'bur-weightage-error' : 'bur-weightage-warning'}`}>
                            {role.uploading_weightage}%
                          </span>
                        </td>
                        <td>
                          <span className="bur-weightage bur-weightage-required">
                            {role.required_weightage}%
                          </span>
                        </td>
                        <td>
                          <span className={`bur-error-badge ${role.error_type === 'exceeds' ? 'bur-error-exceeds' : 'bur-error-insufficient'}`}>
                            {role.error_type === 'exceeds' ? 'Exceeds 100%' : 'Less than 100%'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {renderPagination(failedPage, totalFailedPages, setFailedPage, `${failedRoles.length} failed roles`)}
              <div className="bur-info-box">
                <p>
                  <strong>💡 How to fix:</strong> Adjust the weightage values in your Excel file for the failed roles.
                </p>
                <ul>
                  <li>For each role, the <strong>total weightage</strong> (existing + new) must equal <strong>exactly 100%</strong>.</li>
                  <li>Check the <strong>"Required Weightage"</strong> column - this shows exactly how much you should add.</li>
                </ul>
              </div>
            </div>
          )}

          {/* Successful Roles Section */}
          {hasSuccessfulRoles && (
            <div className="bur-section bur-section-success">
              <h3 className="bur-section-title">
                <span className="bur-section-icon">✅</span>
                Successfully Inserted Roles ({successfulRoles.length})
              </h3>
              <div className="bur-table-container">
                <table className="bur-table bur-table-success">
                  <thead>
                    <tr>
                      <th>Role</th>
                      <th>Department</th>
                      <th>{type === 'questions' ? 'Questions Created' : 'Categories Created'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedSuccessRoles.map((role, idx) => (
                      <tr key={idx}>
                        <td className="bur-role-name">{role.role}</td>
                        <td>{role.department}</td>
                        <td>
                          <span className="bur-count-badge">
                            {type === 'questions' ? role.questions_created : role.categories_created}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {renderPagination(successPage, totalSuccessPages, setSuccessPage, `${successfulRoles.length} successful roles`)}
            </div>
          )}

          {/* Other Errors Section */}
          {data.errors && data.errors.length > 0 && (
            <div className="bur-section bur-section-warning">
              <h3 className="bur-section-title">
                <span className="bur-section-icon">⚠️</span>
                Other Errors ({data.errors.length})
              </h3>
              <ul className="bur-error-list">
                {data.errors.map((error, idx) => (
                  <li key={idx}>{error}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bur-footer">
          <button className="bur-btn bur-btn-primary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkUploadResultPopup;
